import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, X as XIcon, Mail, Send } from "lucide-react";

const TONE_OPTIONS = [
    { value: "info", label: "Information (neutre)" },
    { value: "vacances", label: "Vacances / fermeture" },
    { value: "urgent", label: "Urgent" },
];

const TONE_STYLES = {
    info: { background: "#000000", color: "#F2ECE0", accent: "#D4BC7C" },
    vacances: { background: "#000000", color: "#D4BC7C", accent: "#D4BC7C" },
    urgent: { background: "#D4BC7C", color: "#000000", accent: "#000000" },
};

const TONE_LABEL = { info: "Information", vacances: "Vacances", urgent: "Urgent" };

const DEFAULT = {
    active: false,
    message: "",
    tone: "info",
    start_date: "",
    end_date: "",
    notify_by_email: true,
};

function formatRelative(iso) {
    if (!iso) return null;
    try {
        const then = new Date(iso);
        const diff = (Date.now() - then.getTime()) / 1000;
        if (diff < 60) return `il y a ${Math.round(diff)} s`;
        if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
        if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
        return `le ${then.toLocaleDateString("fr-CH", { day: "numeric", month: "short" })}`;
    } catch {
        return iso.slice(0, 10);
    }
}

export default function AdminAnnouncement() {
    const qc = useQueryClient();
    const [form, setForm] = useState(DEFAULT);

    const { data, isLoading } = useQuery({
        queryKey: ["admin-announcement"],
        queryFn: async () => (await api.get("/admin/announcement")).data,
    });

    useEffect(() => {
        if (data) {
            setForm({
                active: !!data.active,
                message: data.message || "",
                tone: data.tone || "info",
                start_date: data.start_date || "",
                end_date: data.end_date || "",
                notify_by_email: true,
            });
        }
    }, [data]);

    const save = useMutation({
        mutationFn: (payload) => api.put("/admin/announcement", payload),
        onSuccess: (_res, vars) => {
            if (vars.active) {
                toast.success("Bandeau enregistré — visible côté client");
            } else {
                toast.success("Bandeau enregistré — désactivé (non visible)");
            }
            qc.invalidateQueries({ queryKey: ["admin-announcement"] });
            qc.invalidateQueries({ queryKey: ["public-announcement"] });
            window.dispatchEvent(new CustomEvent("bc-announcement-updated"));
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const resend = useMutation({
        mutationFn: () => api.post("/admin/announcement/send-email"),
        onSuccess: () => {
            toast.success("Email en cours d'envoi aux pros validés.");
            qc.invalidateQueries({ queryKey: ["admin-announcement"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const onSave = () => {
        if (form.start_date && form.end_date && form.end_date < form.start_date) {
            toast.error("La date de fin doit être postérieure à la date de début.");
            return;
        }
        if (form.active && !form.message.trim()) {
            toast.error("Le message est obligatoire quand le bandeau est actif.");
            return;
        }
        save.mutate({
            active: form.active,
            message: form.message,
            tone: form.tone,
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            notify_by_email: !!form.notify_by_email,
        });
    };

    const previewTone = TONE_STYLES[form.tone] || TONE_STYLES.info;

    // Compute if the CURRENT SAVED state is actually visible right now (server rules)
    const savedVisibleNow = (() => {
        if (!data || !data.active) return false;
        if (!(data.message || "").trim()) return false;
        const today = new Date().toISOString().slice(0, 10);
        if (data.start_date && today < data.start_date) return false;
        if (data.end_date && today > data.end_date) return false;
        return true;
    })();

    return (
        <div data-testid="admin-announcement">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-serif text-2xl text-[#D4BC7C]">Flash Info — bandeau d'annonce</h2>
                    <p className="text-sm text-[#A0A0A0] mt-2 max-w-2xl">
                        Ce message apparaît en haut de toutes les pages, pour tous les visiteurs. Vous pouvez
                        l'activer, le désactiver ou le programmer sur une plage de dates. Les changements sont
                        appliqués immédiatement.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap" data-testid="announcement-status-wrapper">
                    <span
                        className="text-[10px] uppercase tracking-widest px-3 py-2 border font-medium"
                        style={{
                            color: savedVisibleNow ? "#000" : "#A0A0A0",
                            background: savedVisibleNow ? "#D4BC7C" : "transparent",
                            borderColor: savedVisibleNow ? "#D4BC7C" : "var(--bc-border)",
                        }}
                        data-testid="announcement-status"
                    >
                        {savedVisibleNow ? "● Visible côté client" : "○ Non visible"}
                    </span>
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs uppercase tracking-[0.18em] font-medium text-[#D4BC7C] hover:underline"
                        data-testid="announcement-open-site"
                    >
                        Voir sur le site →
                    </a>
                </div>
            </div>

            {isLoading ? (
                <p className="text-[#A0A0A0] mt-10">Chargement...</p>
            ) : (
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-8">
                    {/* Form */}
                    <div className="bc-card space-y-6" data-testid="announcement-form">
                        {/* Active toggle */}
                        <label className="flex items-center justify-between gap-4 cursor-pointer" data-testid="announcement-active-wrapper">
                            <div>
                                <p className="bc-label mb-1">Bandeau actif</p>
                                <p className="text-xs text-[#A0A0A0]">Affiche/masque immédiatement le bandeau côté public.</p>
                            </div>
                            <Switch
                                checked={form.active}
                                onChange={(v) => setForm({ ...form, active: v })}
                                testId="announcement-active"
                            />
                        </label>

                        <div className="bc-hairline" />

                        {/* Tone */}
                        <div>
                            <label className="bc-label">Style / ton</label>
                            <select
                                className="bc-input"
                                value={form.tone}
                                onChange={(e) => setForm({ ...form, tone: e.target.value })}
                                data-testid="announcement-tone"
                            >
                                {TONE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Message */}
                        <div>
                            <label className="bc-label">Message (1 à 2 phrases)</label>
                            <textarea
                                className="bc-input"
                                rows={4}
                                maxLength={280}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="Ex : La Maison Costa sera fermée du 24 au 26 décembre. Merci de passer vos commandes avant le 20 décembre."
                                data-testid="announcement-message"
                            />
                            <p className="text-xs text-[#A0A0A0] mt-1 text-right">{form.message.length}/280</p>
                        </div>

                        {/* Dates */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="bc-label">Date de début (optionnel)</label>
                                <input
                                    type="date"
                                    className="bc-input"
                                    value={form.start_date}
                                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                    data-testid="announcement-start"
                                />
                            </div>
                            <div>
                                <label className="bc-label">Date de fin (optionnel)</label>
                                <input
                                    type="date"
                                    className="bc-input"
                                    value={form.end_date}
                                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                    data-testid="announcement-end"
                                />
                            </div>
                        </div>
                        <p className="text-xs text-[#A0A0A0] -mt-3">
                            Si les dates sont renseignées, le bandeau s'affichera uniquement entre ces deux dates et se masquera automatiquement après.
                        </p>

                        <div className="bc-hairline" />

                        {/* Email notification */}
                        <div className="p-3 border" style={{ borderColor: "rgba(212,188,124,0.3)", background: "rgba(212,188,124,0.05)" }}>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.notify_by_email}
                                    onChange={(e) => setForm({ ...form, notify_by_email: e.target.checked })}
                                    className="h-4 w-4 accent-[#D4BC7C] mt-0.5"
                                    data-testid="announcement-notify"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <Mail size={14} className="text-[#D4BC7C]" />
                                        <span className="text-sm text-[#F2ECE0] font-medium">Notifier les pros par email</span>
                                    </div>
                                    <p className="text-xs text-[#A0A0A0] mt-1">
                                        À l&apos;enregistrement, un email est envoyé aux pros ayant activé les notifications, si le bandeau vient
                                        d&apos;être activé ou si son message a changé de façon significative.
                                    </p>
                                </div>
                            </label>
                            <div className="mt-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                                <button
                                    type="button"
                                    className="bc-btn bc-btn--sm bc-btn--ghost"
                                    onClick={() => {
                                        if (window.confirm("Envoyer immédiatement un email à tous les pros validés ?")) {
                                            resend.mutate();
                                        }
                                    }}
                                    disabled={resend.isPending || !data?.active || !data?.message}
                                    data-testid="announcement-resend"
                                    title={!data?.active || !data?.message ? "Le bandeau doit être actif et contenir un message enregistré." : "Envoyer maintenant"}
                                >
                                    <Send size={14} /> Envoyer maintenant
                                </button>
                                {data?.last_email_sent_at && (
                                    <span className="text-xs text-[#7a7a7a] flex items-center gap-1.5" data-testid="announcement-last-email">
                                        <Mail size={11} /> Dernier envoi {formatRelative(data.last_email_sent_at)}
                                        {data.last_email_recipients_count != null && ` · ${data.last_email_recipients_count} destinataire${data.last_email_recipients_count > 1 ? "s" : ""}`}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="pt-2">
                            <button
                                className="bc-btn w-full"
                                onClick={onSave}
                                disabled={save.isPending}
                                data-testid="announcement-save"
                            >
                                <Save size={14} /> {save.isPending ? "Enregistrement..." : "Enregistrer"}
                            </button>
                        </div>
                    </div>

                    {/* Live preview */}
                    <div>
                        <p className="bc-label mb-4">Aperçu en direct</p>
                        <div className="border" style={{ borderColor: "var(--bc-border)" }} data-testid="announcement-preview">
                            {form.active && form.message.trim() ? (
                                <PreviewBanner tone={form.tone} message={form.message} />
                            ) : (
                                <div className="p-8 text-center text-sm text-[#A0A0A0]" data-testid="announcement-preview-empty">
                                    {form.active
                                        ? "Renseignez un message pour voir l'aperçu."
                                        : "Le bandeau est désactivé — il ne sera pas visible."}
                                </div>
                            )}
                        </div>

                        <div className="mt-6 text-xs text-[#A0A0A0] leading-relaxed">
                            <p>Comportement côté visiteur :</p>
                            <ul className="list-disc list-inside mt-2 space-y-1">
                                <li>Le bandeau est fixé en haut, au-dessus de l'entête, sur toutes les pages</li>
                                <li>Un bouton × permet de le fermer pour la session en cours</li>
                                <li>À chaque nouvel enregistrement, le bandeau réapparaît (dernière version)</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Switch({ checked, onChange, testId }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            data-testid={testId}
            style={{
                width: "56px",
                height: "30px",
                background: checked ? "#D4BC7C" : "rgba(212,188,124,0.15)",
                border: "1px solid #D4BC7C",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.25s ease",
                flexShrink: 0,
            }}
        >
            <span
                style={{
                    position: "absolute",
                    top: "3px",
                    left: checked ? "28px" : "3px",
                    width: "22px",
                    height: "22px",
                    background: checked ? "#000" : "#D4BC7C",
                    transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
                }}
            />
        </button>
    );
}

function PreviewBanner({ tone, message }) {
    const style = TONE_STYLES[tone] || TONE_STYLES.info;
    return (
        <div
            style={{
                background: style.background,
                color: style.color,
                borderBottom: `1px solid ${style.accent}`,
                width: "100%",
            }}
        >
            <div className="max-w-full flex items-start gap-3 px-4 py-2.5" style={{ minHeight: "42px" }}>
                <span
                    className="hidden sm:inline-flex items-center px-2 py-0.5 flex-shrink-0"
                    style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "0.62rem",
                        fontWeight: 600,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        border: `1px solid ${style.accent}`,
                        color: style.accent,
                        marginTop: "2px",
                    }}
                >
                    {TONE_LABEL[tone]}
                </span>
                <p
                    className="flex-1 min-w-0 text-sm leading-snug"
                    style={{ fontFamily: "Montserrat, sans-serif", overflowWrap: "break-word" }}
                >
                    {message}
                </p>
                <span className="flex-shrink-0 opacity-70" style={{ color: style.color, marginTop: "1px" }}>
                    <XIcon size={16} strokeWidth={2} />
                </span>
            </div>
        </div>
    );
}
